/**
 * A fetch wrapper that retries on transient network errors and limits
 * global concurrency to prevent overwhelming the RPC node.
 *
 * starknet.js's `retries` option only applies to `waitForTransaction` polling,
 * NOT to `fetchEndpoint` which handles all regular RPC calls. This means every
 * transient network hiccup (DNS failure, connection reset, spotty wifi) causes
 * an immediate unhandled error — the most common error on the UI.
 *
 * This wrapper is passed to RpcProvider via the `baseFetch` option so that
 * every RPC call automatically:
 *   1. Waits for a concurrency slot (prevents burst overload)
 *   2. Retries on network-level failures (TypeError from fetch — offline, DNS, connection refused)
 *   3. Retries on HTTP 429 (rate limiting) and HTTP 5xx (server errors)
 */

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 500;
/** Per-request timeout — prevents hung connections from blocking a concurrency slot forever. */
const REQUEST_TIMEOUT_MS = 15_000;

// ---------------------------------------------------------------------------
// Global concurrency limiter
// ---------------------------------------------------------------------------
// The app has ~18 atoms that make RPC calls, many with polling timers. When
// timers align, the page loads, or a browser tab re-focuses, they can fire
// 15+ concurrent requests — overwhelming the RPC node and triggering
// "Failed to fetch" errors. Bounding in-flight requests prevents this.
// ---------------------------------------------------------------------------
const MAX_CONCURRENT_REQUESTS = 6;
let activeRequests = 0;
const waitQueue: Array<() => void> = [];

function acquireSlot(): Promise<void> {
  if (activeRequests < MAX_CONCURRENT_REQUESTS) {
    activeRequests++;
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    waitQueue.push(() => {
      activeRequests++;
      resolve();
    });
  });
}

function releaseSlot(): void {
  if (waitQueue.length > 0) {
    const next = waitQueue.shift()!;
    next();
  } else {
    activeRequests--;
  }
}

// ---------------------------------------------------------------------------
// Retry helpers
// ---------------------------------------------------------------------------

function isNetworkError(error: unknown): boolean {
  // Browsers throw TypeError for network-level failures (offline, DNS, CORS, connection refused).
  // The specific message varies by browser:
  //   Chrome:  TypeError: Failed to fetch
  //   Firefox: TypeError: NetworkError when attempting to fetch resource
  //   Safari:  TypeError: Load failed / The Internet connection appears to be offline
  // AbortController.abort() throws DOMException with name "AbortError".
  if (error instanceof TypeError) return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('networkerror') ||
      msg.includes('failed to fetch') ||
      msg.includes('load failed') ||
      msg.includes('internet connection') ||
      msg.includes('network request failed')
    );
  }
  return false;
}

function isRetryableResponse(response: Response): boolean {
  // 429 Too Many Requests — respect rate limiting
  if (response.status === 429) return true;
  // 5xx — server errors are often transient
  if (response.status >= 500) return true;
  return false;
}

function getRetryDelay(attempt: number, response?: Response): number {
  // Respect Retry-After header if present (for 429s)
  if (response) {
    const retryAfter = response.headers.get('retry-after');
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      if (!isNaN(seconds)) return seconds * 1000;
    }
  }
  // Exponential backoff: 500ms, 1000ms, 2000ms
  return BASE_DELAY_MS * 2 ** attempt;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function resilientFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  await acquireSlot();

  try {
    let lastError: unknown;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      // Apply a per-attempt timeout so a hung connection doesn't block the slot
      // forever. If the caller already supplies an AbortSignal, respect it by
      // aborting on whichever fires first (caller signal or our timeout).
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      // If the caller provided a signal, forward its abort to our controller.
      const callerSignal = init?.signal;
      const onCallerAbort = () => controller.abort();
      callerSignal?.addEventListener('abort', onCallerAbort, { once: true });

      try {
        const response = await fetch(input, { ...init, signal: controller.signal });

        if (isRetryableResponse(response) && attempt < MAX_RETRIES) {
          const waitMs = getRetryDelay(attempt, response);
          console.warn(
            `[resilientFetch] Retryable HTTP ${response.status}, attempt ${attempt + 1}/${MAX_RETRIES}, retrying in ${waitMs}ms`,
          );
          await delay(waitMs);
          continue;
        }

        return response;
      } catch (error) {
        lastError = error;

        if (isNetworkError(error) && attempt < MAX_RETRIES) {
          const waitMs = getRetryDelay(attempt);
          console.warn(
            `[resilientFetch] Network error, attempt ${attempt + 1}/${MAX_RETRIES}, retrying in ${waitMs}ms:`,
            error instanceof Error ? error.message : error,
          );
          await delay(waitMs);
          continue;
        }

        throw error;
      } finally {
        clearTimeout(timeoutId);
        callerSignal?.removeEventListener('abort', onCallerAbort);
      }
    }

    // Should not be reached, but satisfies TypeScript
    throw lastError;
  } finally {
    releaseSlot();
  }
}
